/// <reference types="react-scripts" />
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IChartItem } from "../../../models/chart/chartItem";
import { ISavedChartPanel } from "../../../models/chart/savedChartPanel";
import { IReportParameterValue } from "../../../models/report/reportParameterValue";
import { DashboardChart } from "source/components/framework/DashboardChart";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IDashboardChartData } from "source/models/report/dashboardChartData";

export interface IDocumentPageFilterLists {
    keywords: IIdTitle[];
    dynamicTimeFilters: IIdTitle[];
    documentEndDateDynamicTimeFilters: IIdTitle[];
}

export interface IDocumentPageFilterStateWithoutDates {
    selectedKeywordIds: string[];
    selectedKeywordOperatorId: string;
    selectedDocumentApprovalId: string;
    selectedDocumentContentSearchId: string;
    selectedMaxResultsCount: string;
    selectedDynamicTimeFilterId: string;
    selectedDynamicDocumentEndDateFilterId: string;
}

export interface IDocumentPageFilterState extends IDocumentPageFilterStateWithoutDates {
    selectedStartDate: number;
    selectedEndDate: number
    selectedDocumentEndDateStartDate: number;
    selectedDocumentEndDateEndDate: number
}

export interface IDocumentGeneralFilter {
    keywordIds: string[];
    keywordOperatorId: number;
    documentApprovalId: number;
    documentContentSearchId: number;
    maxResultsCount: number;
    startDate: number;
    endDate: number;
    dynamicTimeFilterId: number;
    documentEndDateStartDate: number;
    documentEndDateEndDate: number
    dynamicDocumentEndDateFilterId: number;
}

export interface IDocumentGeneralFilterChange extends IDocumentGeneralFilter {
    notChanged: boolean;
}

// DocumentPageDashboardChart
export interface IDocumentPageDashboardChartProp {
    chartPanel: ISavedChartPanel;
    chartItems: IChartItem[];
    onShowChartPanelPropertiesDialog: (panelId: string) => void;
}

export interface IDocumentPageDashboardChartState extends IDocumentGeneralFilter {
    isLoading: boolean;
    chartData: IDashboardChartData;
    chartId: string;
    parameterValues: IReportParameterValue[]
}

export class DocumentPageDashboardChart extends React.Component<IDocumentPageDashboardChartProp, IDocumentPageDashboardChartState> {
    static getGeneralFilterFromState = (state: IDocumentPageFilterState): IDocumentGeneralFilter => {
        return {
            keywordIds: state?.selectedKeywordIds ? state.selectedKeywordIds.filter(i => i !== Base.emptyGuid) : [],
            keywordOperatorId: !Base.isNullOrUndefined(state?.selectedKeywordOperatorId) ? parseInt(state.selectedKeywordOperatorId, 10) : 0,
            documentApprovalId: !Base.isNullOrUndefined(state?.selectedDocumentApprovalId) ? parseInt(state.selectedDocumentApprovalId, 10) : 0,
            documentContentSearchId: !Base.isNullOrUndefined(state?.selectedDocumentContentSearchId) ? parseInt(state.selectedDocumentContentSearchId, 10) : 1,
            maxResultsCount: state?.selectedMaxResultsCount ? parseInt(state.selectedMaxResultsCount, 10) : 100,
            startDate: state?.selectedStartDate ? state.selectedStartDate : null,
            endDate: state?.selectedEndDate ? state.selectedEndDate : null,
            dynamicTimeFilterId: state?.selectedDynamicTimeFilterId ? parseInt(state.selectedDynamicTimeFilterId, 10) : 0,
            documentEndDateStartDate: state?.selectedDocumentEndDateStartDate ? state.selectedDocumentEndDateStartDate : null,
            documentEndDateEndDate: state?.selectedDocumentEndDateEndDate ? state.selectedDocumentEndDateEndDate : null,
            dynamicDocumentEndDateFilterId: state?.selectedDynamicDocumentEndDateFilterId ? parseInt(state.selectedDynamicDocumentEndDateFilterId, 10) : 0,
        };
    };

    static getStateFilterPropsFromGeneralFilter = (generalFilter: any): IDocumentPageFilterState => {
        return {
            selectedKeywordIds: generalFilter && generalFilter.keywordIds && generalFilter.keywordIds.length > 0 ? generalFilter.unitIds : [],
            selectedKeywordOperatorId: !Base.isNullOrUndefined(generalFilter?.keywordOperatorId) ? generalFilter.keywordOperatorId.toString(10) : "0",
            selectedDocumentApprovalId: !Base.isNullOrUndefined(generalFilter?.documentApprovalId) ? generalFilter.documentApprovalId.toString(10) : "0",
            selectedDocumentContentSearchId: !Base.isNullOrUndefined(generalFilter?.documentContentSearchId) ? generalFilter.documentContentSearchId.toString(10) : "1",
            selectedMaxResultsCount: generalFilter?.maxResultsCount ? generalFilter.maxResultsCount.toString(10) : "100",
            selectedStartDate: generalFilter?.startDate ? generalFilter.startDate : 0,
            selectedEndDate: generalFilter?.endDate ? generalFilter.endDate : 0,
            selectedDynamicTimeFilterId: !Base.isNullOrUndefined(generalFilter?.dynamicTimeFilterId) ? generalFilter.dynamicTimeFilterId.toString(10) : "0",
            selectedDocumentEndDateStartDate: generalFilter?.documentEndDateStartDate ? generalFilter.documentEndDateStartDate : 0,
            selectedDocumentEndDateEndDate: generalFilter?.documentEndDateEndDate ? generalFilter.documentEndDateEndDate : 0,
            selectedDynamicDocumentEndDateFilterId: !Base.isNullOrUndefined(generalFilter?.dynamicDocumentEndDateFilterId) ? generalFilter.dynamicDocumentEndDateFilterId.toString(10) : "0",
        };
    };

    static getGeneralFilterNotChanged = (prevFilter: IDocumentGeneralFilter, filter: IDocumentGeneralFilter): boolean => {
        return (prevFilter.startDate ?? -99).toString(10) === (filter.startDate ?? -99).toString(10) &&
            (prevFilter.endDate ?? -99).toString(10) === (filter.endDate ?? -99).toString(10) &&
            (prevFilter.documentEndDateStartDate ?? -99).toString(10) === (filter.documentEndDateStartDate ?? -99).toString(10) &&
            (prevFilter.documentEndDateEndDate ?? -99).toString(10) === (filter.documentEndDateEndDate ?? -99).toString(10) &&
            (prevFilter.keywordOperatorId ?? -99).toString(10) === (filter.keywordOperatorId ?? -99).toString(10) &&
            (prevFilter.documentApprovalId ?? -99).toString(10) === (filter.documentApprovalId ?? -99).toString(10) &&
            (prevFilter.documentContentSearchId ?? -99).toString(10) === (filter.documentContentSearchId ?? -99).toString(10) &&
            (prevFilter.maxResultsCount ?? -99).toString(10) === (filter.maxResultsCount ?? -99).toString(10) &&
            Base.equalStrArrays(prevFilter.keywordIds, filter.keywordIds) &&
            prevFilter.dynamicTimeFilterId.toString(10) === filter.dynamicTimeFilterId.toString(10) &&
            prevFilter.dynamicDocumentEndDateFilterId.toString(10) === filter.dynamicDocumentEndDateFilterId.toString(10);
    };

    static getDocumentPageDashboardChartStateNotChanged = (prevState: IDocumentPageDashboardChartState, state: IDocumentPageDashboardChartState): boolean => {
        return prevState.chartId === state.chartId &&
            DocumentPageDashboardChart.getGeneralFilterNotChanged(prevState, state) &&
            JSON.stringify(prevState.parameterValues) === JSON.stringify(state.parameterValues);
    };

    static getDocumentGeneralFilterChange(prevProps: IDocumentPageFilterState, props: IDocumentPageFilterState): IDocumentGeneralFilterChange {
        const prevFilter = DocumentPageDashboardChart.getGeneralFilterFromState(prevProps);
        const filter = DocumentPageDashboardChart.getGeneralFilterFromState(props);
        return {
            ...filter,
            notChanged: DocumentPageDashboardChart.getGeneralFilterNotChanged(prevFilter, filter)
        };
    }

    getStateChangeFromProps = (props: IDocumentPageDashboardChartProp): any => {
        const chartItem = props.chartPanel && props.chartItems
            ? props.chartItems.find(i => i.reportName === props.chartPanel.reportId)
            : null;
        const reportFilterProps = DocumentPageDashboardChart.getStateFilterPropsFromGeneralFilter(props.chartPanel?.generalFilter);
        const result = {
            keywordIds: reportFilterProps.selectedKeywordIds ? reportFilterProps.selectedKeywordIds : [],
            keywordOperatorId: reportFilterProps.selectedKeywordOperatorId ? parseInt(reportFilterProps.selectedKeywordOperatorId, 10) : 0,
            documentApprovalId: reportFilterProps.selectedDocumentApprovalId ? parseInt(reportFilterProps.selectedDocumentApprovalId, 10) : 0,
            documentContentSearchId: reportFilterProps.selectedDocumentContentSearchId ? parseInt(reportFilterProps.selectedDocumentContentSearchId, 10) : 1,
            maxResultsCount: reportFilterProps.selectedMaxResultsCount ? parseInt(reportFilterProps.selectedMaxResultsCount, 10) : 100,
            chartId: props.chartPanel?.chartId ?? "",
            dynamicTimeFilterId: reportFilterProps.selectedDynamicTimeFilterId,
            startDate: reportFilterProps.selectedStartDate ? reportFilterProps.selectedStartDate : null,
            endDate: reportFilterProps.selectedEndDate ? reportFilterProps.selectedEndDate : null,
            dynamicDocumentEndDateFilterId: reportFilterProps.selectedDynamicDocumentEndDateFilterId,
            documentEndDateStartDate: reportFilterProps.selectedDocumentEndDateStartDate ? reportFilterProps.selectedDocumentEndDateStartDate : null,
            documentEndDateEndDate: reportFilterProps.selectedDocumentEndDateEndDate ? reportFilterProps.selectedDocumentEndDateEndDate : null,
            parameterValues: props.chartPanel && chartItem ? props.chartPanel.getReportParameterValues(chartItem) : []
        };
        return result;
    };

    constructor(props: IDocumentPageDashboardChartProp) {
        super(props);
        this.state = {
            ...this.getStateChangeFromProps(props),
            chart: "",
            isLoading: true
        };
    }

    createChart = async () => {
        //const props = this.props;
        //const state = this.state;
        //if (!props.chartPanel || !props.chartItems || state.parameterValues.length < 1) return;
        // this.setState({ isLoading: true });
        // const chart = await AppUtils.callService(() => taskService.getChart(props.chartPanel.reportId, props.chartPanel.chartId,
        //     state.startDate, state.endDate, state.dynamicTime, state.unitIds, state.categoryIds, state.handlerUserCodes, state.state, state.severity, state.resolution,
        //     state.source, state.anonymity, state.parameterValues), false, true, () => { this.setState({ isLoading: false }); });
        // this.setState({
        //     chart
        // });
    };

    componentDidUpdate(prevProps: IDocumentPageDashboardChartProp) {
        const state = this.state;
        const newState = this.getStateChangeFromProps(this.props);
        if (DocumentPageDashboardChart.getDocumentPageDashboardChartStateNotChanged(state, newState)) return;
        this.setState({
            ...newState
        }, this.createChart);
    }

    componentDidMount(): void {
        this.createChart();
    }
    
    render() {
        const props = this.props;
        const state = this.state;
        const chartPanel = props.chartPanel;
        if (!chartPanel) return null;
        return (
            <DashboardChart
                panelId={chartPanel.id}
                title={chartPanel.getTitle()}
                chartData={state.chartData}
                isLoading={state.isLoading}
                onShowProperties={props.onShowChartPanelPropertiesDialog}
            />

        );
    }
}